import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { promotionApi } from '../api/promotionApi';
import { useAuth } from '../auth/useAuth';
import BrandLockup from '../components/BrandLockup';
import { useProfile } from '../context/ProfileContext';
import { rememberPromoCampaign } from '../promoCampaign';
import { PromoCampaign } from '../types';

type Lookup =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'unknown' }
  | { status: 'found'; campaign: PromoCampaign };

const LOADING: Lookup = { status: 'loading' };

// What the trial includes. This is fixed copy, not read from the plan, so keep it in line with what
// the campaign's plan actually gives.
const FEATURES = [
  { icon: 'pi-camera', title: 'Unlimited ball lie photo uploads', detail: "Snap your lie and hear your caddie's advice." },
  { icon: 'pi-comments', title: 'Caddie Chats', detail: 'Ask your caddie a question by voice or text.' },
  { icon: 'pi-map-marker', title: 'GPS range finder', detail: 'See your distance to the green as you play.' },
];

const OfferShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="onboarding-page">
    <div
      className="onboarding-page__scene"
      aria-hidden="true"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/onboarding-bg.jpg)` }}
    />
    <div className="onboarding-page__shade" aria-hidden="true" />
    <section className="onboarding-card onboarding-card--narrow promo-landing">
      <div className="onboarding-card__brand"><BrandLockup tone="onLight" compact /></div>
      {children}
    </section>
  </div>
);

/**
 * The public offer page behind a promo campaign link or QR code (`/promo/:slug`). It needs no
 * account: a new golfer reads the offer, accepts it, and only then is taken on to create an account.
 * Accepting remembers the campaign, so the sign-up screens and onboarding carry the offer through and
 * the free trial starts when the golfer finishes onboarding. A campaign that has ended (or never
 * existed) says so, and a golfer who already has an account is told the offer is for new golfers.
 */
const PromoLandingPage: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isHydrated, isVerificationHydrated, hasMembership, verification } = useProfile();
  const [lookup, setLookup] = useState<Lookup>(LOADING);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLookup(LOADING);
    promotionApi.getCampaign(slug)
      .then((campaign) => {
        if (!cancelled) setLookup(campaign ? { status: 'found', campaign } : { status: 'unknown' });
      })
      .catch(() => {
        if (!cancelled) setLookup({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, attempt]);

  // Whether the visitor already has an account is only known once the session and profile have loaded.
  const checkingAccount = authLoading || (isAuthenticated && !(isHydrated && isVerificationHydrated));
  const isSetUp = isAuthenticated && hasMembership && Boolean(verification?.isProfileConfirmed);
  const goToApp = () => navigate('/', { replace: true });

  if (lookup.status === 'failed') {
    return (
      <OfferShell>
        <div className="onboarding-card__header">
          <h1>We couldn&apos;t load this offer</h1>
          <p>Check your connection and try again.</p>
        </div>
        <Button label="Try again" icon="pi pi-refresh" className="onboarding-btn" onClick={() => setAttempt((count) => count + 1)} />
        <button type="button" className="onboarding-link" onClick={goToApp}>Continue to CaddieSight</button>
      </OfferShell>
    );
  }

  if (lookup.status === 'unknown' || (lookup.status === 'found' && !lookup.campaign.active)) {
    const ended = lookup.status === 'found' ? lookup.campaign : null;
    return (
      <OfferShell>
        <i className="pi pi-exclamation-triangle promo-landing__icon" aria-hidden="true" />
        <div className="onboarding-card__header">
          <h1>{ended ? `${ended.name} has ended` : 'Link not found'}</h1>
          <p>
            {ended ? 'This offer is no longer available.' : 'This link is not valid.'}{' '}
            You can still sign up for CaddieSight.
          </p>
        </div>
        <Button label="Continue to CaddieSight" className="onboarding-btn" onClick={goToApp} />
      </OfferShell>
    );
  }

  // Wait for both the offer and who is looking at it, so the page never flashes the wrong state.
  if (lookup.status === 'loading' || checkingAccount) {
    return (
      <OfferShell>
        <div className="promo-landing__status" role="status">
          <ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="4" />
          <p>Opening your offer…</p>
        </div>
      </OfferShell>
    );
  }

  const { campaign } = lookup;

  if (isSetUp) {
    return (
      <OfferShell>
        <div className="onboarding-card__header">
          <h1>{campaign.name}</h1>
          {campaign.message ? <p>{campaign.message}</p> : null}
        </div>
        <p className="onboarding-note onboarding-note--info">
          You already have a CaddieSight account, and this offer is for new golfers.
        </p>
        <Button label="Open CaddieSight" icon="pi pi-arrow-right" iconPos="right" className="onboarding-btn" onClick={goToApp} />
      </OfferShell>
    );
  }

  // Remembering the campaign is what accepting means: sign-up and onboarding pick the offer up from
  // here, and the API starts the trial when onboarding finishes. A signed-in golfer who has not
  // finished setting up goes straight back into that setup; everyone else creates an account first.
  const accept = () => {
    rememberPromoCampaign(campaign.slug);
    navigate(isAuthenticated ? '/play' : '/login');
  };

  return (
    <OfferShell>
      <span className="promo-landing__eyebrow">
        <i className="pi pi-gift" aria-hidden="true" />
        Limited-time offer
      </span>
      <div className="onboarding-card__header">
        <h1>{campaign.name}</h1>
        {campaign.message ? <p>{campaign.message}</p> : null}
      </div>
      <div className="promo-landing__trial">
        <span className="promo-landing__trial-days">{campaign.trialDays} days free</span>
        <span className="promo-landing__trial-note">No payment needed</span>
      </div>
      <ul className="promo-landing__features" aria-label="What's included">
        {FEATURES.map((feature) => (
          <li key={feature.title}>
            <i className={`pi ${feature.icon} promo-landing__feature-icon`} aria-hidden="true" />
            <strong>{feature.title}</strong>
            <span>{feature.detail}</span>
          </li>
        ))}
      </ul>
      <Button
        label="Claim my free trial"
        icon="pi pi-arrow-right"
        iconPos="right"
        className="onboarding-btn"
        onClick={accept}
      />
      <p className="promo-landing__fine">
        {isAuthenticated
          ? "Next, you'll finish setting up your account."
          : "Next, you'll create your free CaddieSight account."}{' '}
        When the trial ends you keep a free account, and there is nothing to cancel.
      </p>
      {isAuthenticated ? null : (
        <p className="promo-landing__fine">
          Already have an account?{' '}
          <button type="button" className="onboarding-link" onClick={() => navigate('/login')}>Sign in</button>
        </p>
      )}
    </OfferShell>
  );
};

export default PromoLandingPage;
